import { useEffect, useState } from "react"
import { Check, X } from "lucide-react"
import type { CatalogItem, SelectedPackage } from "../types"
import { formatPrice, savingNet } from "../lib/format"
import { SaveBadge } from "./ui"

interface Props {
  item: CatalogItem | null
  requiredCount: number
  /** Bereits im Einzelbezug gewaehlte Pakete uebernehmen. */
  initial?: SelectedPackage[]
  infopackages: CatalogItem[]
  catalog: CatalogItem[]
  onCancel: () => void
  onConfirm: (selected: SelectedPackage[]) => void
}

/** Bundle/Staffel: genau N Infopakete waehlen, bevor die Position in den Warenkorb geht. */
export function PackageSelector({ item, requiredCount, initial, infopackages, catalog, onCancel, onConfirm }: Props) {
  const [picked, setPicked] = useState<SelectedPackage[]>([])

  useEffect(() => {
    setPicked((initial ?? []).slice(0, requiredCount))
  }, [item?.price_id, initial, requiredCount])

  useEffect(() => {
    if (!item) return
    const onKey = (e: KeyboardEvent) => e.key === "Escape" && onCancel()
    window.addEventListener("keydown", onKey)
    return () => window.removeEventListener("keydown", onKey)
  }, [item, onCancel])

  if (!item) return null

  const full = picked.length >= requiredCount
  const saving = savingNet(item, catalog)
  const staffel = item.bundle_type === "staffel"

  function toggle(p: CatalogItem) {
    const id = p.package_id
    if (!id) return
    setPicked((prev) => {
      const hit = prev.find((x) => x.package_id === id)
      if (hit) return prev.filter((x) => x.package_id !== id)
      if (prev.length >= requiredCount) return prev
      return [...prev, { package_id: id, name: p.name }]
    })
  }

  return (
    <div className="fixed inset-0 z-40 grid items-start justify-center overflow-y-auto p-5 pt-16 md:pt-32">
      <div className="fixed inset-0 bg-[rgba(60,58,64,0.6)]" onClick={onCancel} aria-hidden />
      <div
        role="dialog"
        aria-modal="true"
        aria-label={`${item.name}: Infopakete wählen`}
        className="relative z-10 w-[732px] max-w-[calc(100vw-40px)] rounded-card bg-white p-5 shadow-panel md:p-7"
      >
        <div className="flex items-start justify-between gap-4">
          <div>
            <h3 className="text-lg font-medium">
              {staffel ? `Staffelpreis für ${requiredCount} Pakete` : `${item.name} – Infopakete wählen`}
            </h3>
            <p className="mt-1 text-sm text-brand-muted">Bitte wählen Sie die gewünschten Infopakete.</p>
          </div>
          <button onClick={onCancel} aria-label="Schließen" className="rounded-full p-1 text-brand-muted hover:bg-surface">
            <X className="h-5 w-5" />
          </button>
        </div>

        <div className="mt-6 grid gap-3 md:grid-cols-2 md:gap-x-6">
          {infopackages.map((p) => {
            const on = picked.some((x) => x.package_id === p.package_id)
            const blocked = full && !on
            return (
              <button
                key={p.price_id}
                type="button"
                role="checkbox"
                aria-checked={on}
                disabled={blocked}
                onClick={() => toggle(p)}
                className={`flex w-full items-center gap-3 rounded-field border px-4 py-3 text-left transition ${
                  on ? "border-brand-blue bg-brand-tint" : "border-line hover:border-brand-blue"
                } ${blocked ? "cursor-not-allowed opacity-45 hover:border-line" : ""}`}
              >
                <span
                  className={`flex h-[18px] w-[18px] shrink-0 items-center justify-center rounded-pick border ${
                    on ? "border-brand-blue bg-brand-blue" : "border-line bg-white"
                  }`}
                >
                  {on && <Check className="h-3 w-3 text-white" strokeWidth={3.2} />}
                </span>
                <span className="flex-1 text-sm">{p.name}</span>
              </button>
            )
          })}
        </div>

        <div className="mt-6 border-t border-line pt-5">
          <div className="flex flex-wrap items-center gap-3">
            <span className="flex-1 text-sm font-medium">{staffel ? "Staffelpreis" : "Preis"}</span>
            {saving != null && <SaveBadge amount={formatPrice(saving, item.currency)} />}
            <span className="text-xl font-medium text-brand-blue md:text-2xl">
              {formatPrice(item.amount_net, item.currency)}
            </span>
          </div>

          <div className="mt-4 flex flex-wrap items-center justify-between gap-3">
            <span className="text-sm text-brand-muted">
              {picked.length} / {requiredCount} gewählt
            </span>
            <div className="flex flex-1 gap-3 md:flex-none">
              <button
                onClick={onCancel}
                className="flex-1 rounded-field bg-surface px-6 py-2.5 text-sm font-medium transition hover:bg-line md:flex-none"
              >
                Abbrechen
              </button>
              <button
                disabled={picked.length !== requiredCount}
                onClick={() => onConfirm(picked)}
                className="flex-1 rounded-field bg-brand-blue px-6 py-2.5 text-sm font-medium text-white transition hover:brightness-105 disabled:cursor-not-allowed disabled:opacity-45 md:flex-none"
              >
                In den Warenkorb
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}
